import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';

import { AutorizationResponseType, TDate } from '@/shared/types/app.types';
import { IGuest } from '@/shared/types/booking.types';
import { IBookingObjects, IObject } from '@/shared/types/bookingObjects.types';

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(isBetween);

export const getSession = () => {
	const session = JSON.parse(localStorage.getItem('user') ?? '{}') as AutorizationResponseType;
	if (session) return session;
};

export const isActiveBooking = (start: string, end: string) =>
	dayjs().isBetween(dayjs(start), dayjs(end));

export const isNearestBooking = (start: string) => {
	const diff = dayjs(start).diff(dayjs(), 'minute');
	return diff > 0 && diff <= 360;
};

export const isFutureBooking = (start: string) => dayjs(start).isAfter(dayjs());

export const isEndedBooking = (end: string) => dayjs(end).isBefore(dayjs());

export const isActiveGuest = (guest: IGuest | undefined | null): guest is IGuest =>
	!!guest?.full_name?.trim() && !guest.is_deleted && !guest.is_hard_deleted;

export const resetTime = (date: dayjs.ConfigType) =>
	dayjs(date).set('h', 0).set('m', 0).set('s', 0).set('ms', 0);

export const calcObjectLoad = (booked: number, total: number, zero: number = 0) => {
	if (booked && total) {
		return Number(String(Math.floor((booked / (total / 100) / 100) * 100))).toFixed(zero);
	}

	return null;
};

export const getLocationType = (type: string) => {
	if (type === 'Территория') return 'subarea';
	if (type === 'Парковка') return 'parking';
	if (type === 'Этаж') return 'floor';
	if (type === 'Административно - хозяйственные места') return 'administrative';
	return '';
};

export const getUserName = (fullName: string, mode: 'full' | 'first' | 'initials') => {
	if (!fullName) return '';

	switch (mode) {
		case 'full':
			return fullName.split(' ').slice(0, 2).join(' ');
		case 'first':
			return fullName.split(' ')[1];
		case 'initials':
			return fullName
				.split(' ')
				.map((name) => name.charAt(0))
				.slice(0, 2)
				.join('');
	}
};

export const getPlaceMapPathClass = (
	obj: IBookingObjects,
	personId?: number | undefined,
	isAdmin?: boolean,
	isModerator?: boolean,
) => {
	if (!obj.type.is_bookable) {
		return 'interactive';
	}

	if (obj.is_blocked) {
		return 'disabled';
	}

	// if (!obj.type.is_bookable || obj.is_blocked) {
	// 	return 'disabled';
	// }

	if (obj.for_moderator_only && (isAdmin || isModerator)) {
		return 'moderator';
	}

	if (
		(isAdmin || isModerator) &&
		obj.booking_status === 'free' &&
		obj.type.is_bookable &&
		!obj.owner_person_id
	) {
		//TODO: Отрисовка объектов для модераторов с персональным местом того же типа на том же этаже, что и объект.
		// Данное условие временное решение до изменения ролевой модели на стороне бэкэнда.
		return 'available';
	}

	if (obj.is_available_for_person) {
		if (obj.owner_person_id === personId) {
			return 'owner';
		}

		if (obj.booking_status !== 'free') {
			if (
				obj.owner_person_id === personId
				// obj.bookings?.some((booking) => booking.initiator?.id === personId)
			) {
				return 'selfBooked';
			}

			return 'unavailable';
		}

		return 'available';
	} else {
		if (obj.owner_person_id) {
			if (isAdmin || isModerator) {
				return 'personal';
			}
			return 'unavailable';
		}
	}

	return 'unavailable';
};

export const chunk = <T>(array: T[], size: number): T[][] => {
	if (!array.length) {
		return [];
	}
	const head = array.slice(0, size);
	const tail = array.slice(size);
	return [head, ...chunk(tail, size)];
};

export const getMembers = (members: IObject[]): IObject[] => {
	let children: IObject[] = [];

	return members
		?.map((m) => {
			if (m?.children && m?.children.length) {
				children = [...children, ...m.children];
			}
			return m;
		})
		.concat(children?.length ? getMembers(children) : children);
};

export const getObjectHierarchy = (object: IObject) =>
	getMembers([object])
		.slice(0, -1)
		.map((object) => object.name)
		.join(' • ');

export const getTaxiPoints = (objects: IObject[]) =>
	objects?.filter((object) => object.name.includes('Место посадки'));

export const getFilteredOptions = (data: string[], searchQuery: string, limit: number) => {
	const result: string[] = [];

	for (let i = 0; i < data.length; i += 1) {
		if (result.length === limit) {
			break;
		}

		if (data[i].toLowerCase().includes(searchQuery.trim().toLowerCase())) {
			result.push(data[i]);
		}
	}

	return result;
};

export const getImageScale = (
	imageWidth: number,
	imageHeight: number,
	wrapperWidth: number,
	wrapperHeight: number,
) => {
	return Math.min(wrapperWidth / imageWidth, wrapperHeight / imageHeight);
};

export const setTimeToDate = (sourceDate: TDate, targetDate: TDate) => {
	const hours = dayjs(sourceDate).hour();
	const minutes = dayjs(sourceDate).minute();
	const seconds = dayjs(sourceDate).second();

	targetDate = dayjs(targetDate).hour(hours).minute(minutes).second(seconds).millisecond(0);

	return targetDate.format();
};

export const stringifyObjectValues = (object: Record<string | number | symbol, unknown>) => {
	Object.entries(object).forEach(([key, value]) => {
		if (value) return (object[key] = JSON.stringify(value));
	});
};

export const removeAttributes = <T extends Record<string, any>>(
	data: T | T[],
	props: Array<keyof T>,
): T | T[] | null => {
	if (Array.isArray(data)) {
		return data.map((item) => removeAttributes(item, props)).filter((item) => item !== null) as T[];
	}

	const exclude = ['work_place', 'meeting_room', 'parking_place'];

	if (typeof data === 'object' && data !== null) {
		if (exclude.includes(data.type.kind)) {
			return null;
		}

		const cleanedObject: Partial<T> = {};

		for (const key of props) {
			if (data.hasOwnProperty(key)) {
				cleanedObject[key] = data[key];
			}
		}

		if ('children' in data && Array.isArray((data as any).children)) {
			// @ts-ignore
			cleanedObject.children = removeAttributes((data as any).children, props) as any;
		}

		return cleanedObject as T;
	}

	return data as T;
};
